import { useEffect, useState } from "react";
import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Input, Textarea } from "@/components";
import type { ApiDeviceProfile } from "../../_types/device-profiles.types";
import { createPrototypeProfileId } from "../../_constants/device-profile-prototypes.const";
import styles from "./CreatePolicyDrawer.module.scss";

const DEFAULT_CONFIG = `{
  "schemaVersion": 1
}`;

type CreatePolicyDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (profile: ApiDeviceProfile) => void;
};

export const CreatePolicyDrawer = ({ isOpen, onClose, onCreate }: CreatePolicyDrawerProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [configText, setConfigText] = useState(DEFAULT_CONFIG);
  const [nameError, setNameError] = useState("");
  const [configError, setConfigError] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setDescription("");
    setConfigText(DEFAULT_CONFIG);
    setNameError("");
    setConfigError("");
    setSubmitError(null);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (name.trim().length < 1) {
      setNameError("Name is required");
      return;
    }
    setNameError("");
    let config: Record<string, unknown>;
    try {
      config = JSON.parse(configText) as Record<string, unknown>;
    } catch {
      setConfigError("Invalid JSON");
      return;
    }
    setConfigError("");
    setSubmitError(null);
    const now = Date.now();
    onCreate({
      id: createPrototypeProfileId(),
      name: name.trim(),
      description: description.trim() || null,
      config,
      configVersion: 1,
      createdAt: now,
      updatedAt: now,
    });
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Create device profile (prototype)">
      <DrawerHeader title="Create profile (Prototype)" onClose={handleClose} />
      <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
        <DrawerBody>
          <div className={styles["comp"]}>
            <p className={styles["comp__hint"]}>
              Preview only — changes stay in this page session and are never sent to the API or
              assigned to real devices.
            </p>
            <div className={styles["comp__fields"]}>
              <Input
                id="create-profile-name"
                label="Name"
                value={name}
                placeholder="e.g. Kiosk — reception"
                required
                error={nameError}
                onChange={(v) => {
                  setName(v);
                  setNameError("");
                  setSubmitError(null);
                }}
              />

              <Input
                id="create-profile-description"
                label="Description"
                value={description}
                placeholder="Optional"
                onChange={(v) => setDescription(v)}
              />

              <Textarea
                id="create-profile-config"
                className={styles["comp__textarea"]}
                label="Config (JSON)"
                value={configText}
                onChange={(next) => {
                  setConfigText(next);
                  setConfigError("");
                  setSubmitError(null);
                }}
                rows={14}
                spellCheck={false}
                error={configError}
              />
            </div>

            {submitError ? <div className={styles["comp__error"]}>{submitError}</div> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Create profile
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};
